// Utilitário de leitura de JWT — SEM verificação de assinatura.
//
// IMPORTANTE: nada aqui prova autenticidade. Decodificar o payload no browser
// só serve para LER campos informativos (ex.: `exp`) e agendar um refresh
// proativo. A validação real do token é responsabilidade do backend. Nunca use
// estes dados para decidir permissão/autorização no frontend.
//
// O token da Fatura Web hoje aparenta ser opaco (é rotacionado a cada resposta,
// o que um JWT stateless não precisaria). Estas funções foram feitas para
// DEGRADAR com elegância: se o token não for um JWT, retornam null e o resto do
// app segue no fluxo reativo (refresh no 401). Se um dia o backend passar a
// emitir JWT, o refresh proativo por `exp` liga sozinho.

// Base64URL -> string, tolerante a padding ausente.
function base64UrlDecode(segment) {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  // atob lida com base64 padrão; decodeURIComponent recupera UTF-8.
  const binary = atob(padded);
  try {
    return decodeURIComponent(
      binary
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
  } catch {
    return binary;
  }
}

/**
 * Decodifica o payload de um JWT (parte do meio). Retorna o objeto ou null se
 * o valor não tiver o formato de JWT (3 segmentos) ou o payload não for JSON.
 */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null; // não é JWT — provavelmente token opaco
  try {
    const json = base64UrlDecode(parts[1]);
    const payload = JSON.parse(json);
    return payload && typeof payload === "object" ? payload : null;
  } catch {
    return null;
  }
}

/** true se `token` tem a cara de um JWT com payload JSON decodificável. */
export function isJwt(token) {
  return decodeJwtPayload(token) !== null;
}

/**
 * Instante de expiração do token em milissegundos (epoch), ou null se não for
 * JWT ou não tiver `exp`. `exp` no JWT é em segundos (RFC 7519).
 */
export function getTokenExpiryMs(token) {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return null;
  return payload.exp * 1000;
}

/**
 * Milissegundos até a expiração (pode ser negativo se já expirou), ou null se
 * o token não carregar `exp`. `nowMs` é injetável para facilitar testes.
 */
export function getMsUntilExpiry(token, nowMs) {
  const expMs = getTokenExpiryMs(token);
  if (expMs === null) return null;
  const base = typeof nowMs === "number" ? nowMs : Date.now();
  return expMs - base;
}
